'use client';

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { qurvo, datadogUser } from '@/bootstrap/analyticsQueue';
import { BlockedScreen } from '@/components/BlockedScreen';
import { SessionExpiredScreen } from '@/components/SessionExpiredScreen';
import { ErrorMessage } from '@/components/ErrorMessage';
import { useApiClient } from '@/contexts/ApiContext';
import { useAuthStore } from '@/stores/authStore';
import { apiErrorCode, INIT_DATA_EXPIRED } from '@/utils/apiError';
import { useDeviceSignalReport } from '@/hooks/useDeviceSignalReport';
import { getDeviceAccounts } from '@/utils/deviceAccounts';
import { useRawInitDataWithDev } from '@/hooks/useRawInitDataWithDev';
import { useProfileUpdater } from '@/queries/useProfileQueries';
import { useSetLanguage, type Language } from '@/stores/languageStore';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import translations from '@/AppContent.translations';
import { useRuntime } from '@/runtime/RuntimeContext';
import { getApiBaseUrl } from '@/utils/apiBaseUrl';
import {
  extractStartParam,
  extractInventoryItemId,
  extractPromocodeParam,
  extractUserId,
  isProfileParam,
  isInventoryParam,
  isWheelParam,
  isReferralsParam,
  isRushMidSurveyParam,
} from '@/utils/telegram';

export type AuthBootstrapStatus = 'loading' | 'ready' | 'error' | 'blocked' | 'expired';

interface AuthBootstrapState {
  status: AuthBootstrapStatus;
  error: string;
  retry: () => void;
}

export const AuthBootstrapContext = createContext<AuthBootstrapState | null>(null);

export function useAuthBootstrap(): AuthBootstrapState {
  const ctx = useContext(AuthBootstrapContext);
  if (!ctx) {
    throw new Error('useAuthBootstrap must be used within AuthBootstrap');
  }
  return ctx;
}

function resolveDeepLinkTarget(
  startParam: string | null,
  hasOpenedCases: boolean,
  runtime: 'tma' | 'web',
): string | null {
  // Onboarding flow is TMA-only — on web first-time visitors land on
  // the public marketing page, sign in, and skip straight to the
  // authed shell.
  if (!hasOpenedCases && runtime === 'tma') return '/onboarding/welcome';

  const inventoryItemId = extractInventoryItemId(startParam);
  if (inventoryItemId) return `/inventory/${encodeURIComponent(inventoryItemId)}`;

  const promocodeValue = extractPromocodeParam(startParam);
  if (promocodeValue !== null) {
    return promocodeValue
      ? `/promocode?code=${encodeURIComponent(promocodeValue)}`
      : '/promocode';
  }

  const otherUserId = extractUserId(startParam);
  if (otherUserId) return `/profile/${encodeURIComponent(otherUserId)}`;

  if (isProfileParam(startParam)) return '/profile';
  if (isInventoryParam(startParam)) return '/inventory';
  if (isWheelParam(startParam)) return '/wheel';
  if (isReferralsParam(startParam)) return '/referrals';
  if (isRushMidSurveyParam(startParam)) return '/games/rush-mid-survey';

  return null;
}

export function AuthBootstrap({ children }: { children: ReactNode }) {
  useDeviceSignalReport();
  const { t } = useLocalTranslation(translations);
  const apiClient = useApiClient();
  const rawInitData = useRawInitDataWithDev();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { setProfile } = useProfileUpdater();
  const markAuthed = useAuthStore((s) => s.markAuthed);
  const setLanguage = useSetLanguage();
  const router = useRouter();
  const runtime = useRuntime();

  const [error, setError] = useState('');
  const deepLinkAppliedRef = useRef(false);

  const isAnonymous = !rawInitData && !isAuthenticated;

  // ?handoff=<token> in the URL means the visitor came from the TMA
  // "Open in browser" flow. We need to redeem before deciding whether
  // to show the anon UI, so default to loading in that case.
  const initialHasHandoff =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('handoff');
  const [status, setStatus] = useState<AuthBootstrapStatus>(
    isAnonymous && !initialHasHandoff ? 'ready' : 'loading',
  );

  const runGenRef = useRef(0);

  const run = async () => {
    const myGen = ++runGenRef.current;
    const isFresh = () => runGenRef.current === myGen;

    // Web handoff: TMA settings opened the web shell with
    // ?handoff=<opaque token>. The backend stored the principal in
    // Redis at issue time; redeem it (atomic GETDEL → session cookie),
    // strip the param, then proceed via the cookie path.
    let handoffApplied = false;
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const handoff = url.searchParams.get('handoff');
      if (handoff) {
        url.searchParams.delete('handoff');
        const newUrl = url.pathname + (url.search || '') + url.hash;
        window.history.replaceState({}, '', newUrl);
        try {
          const res = await fetch(
            `${getApiBaseUrl()}/api/auth/web-handoff/redeem`,
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'shot',
              },
              body: JSON.stringify({ token: handoff }),
            },
          );
          if (!isFresh()) return;
          if (res.ok) {
            const data = (await res.json()) as { userId: string };
            markAuthed(data.userId);
            handoffApplied = true;
          }
        } catch (err) {
          console.error('[AuthBootstrap] handoff redeem failed:', err);
        }
      }
    }

    // After potential handoff, re-evaluate. `isAuthenticated` from the
    // closure is stale — read from the store directly.
    const authedNow = handoffApplied || useAuthStore.getState().isAuthenticated;
    const stillAnon = !rawInitData && !authedNow;

    if (stillAnon) {
      if (isFresh()) setStatus('ready');
      return;
    }
    setStatus('loading');
    setError('');

    try {
      const startParam = extractStartParam(rawInitData);

      // TMA cold open: hand initData to the backend. It validates HMAC,
      // creates / reuses the user, and sets the httpOnly cookie via
      // Set-Cookie. Idempotent — safe to call on every mount; if the
      // existing cookie already matches the initData identity the
      // backend returns 200 without rotating the session.
      if (rawInitData) {
        try {
          const res = await fetch(`${getApiBaseUrl()}/api/auth/tma`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'shot',
            },
            body: JSON.stringify({ initData: rawInitData, u: getDeviceAccounts() }),
          });

          if (!isFresh()) return;

          if (res.status === 403) {
            const body = await res.json().catch(() => null);
            const code = (body as { error?: { error?: { code?: string } } })?.error?.error?.code;
            if (code === 'USER_BLOCKED') {
              setStatus('blocked');
              return;
            }
          }

          if (res.status === 401) {
            const body: unknown = await res.json().catch(() => null);
            if (apiErrorCode(body) === INIT_DATA_EXPIRED) {
              useAuthStore.getState().markSessionExpired();
              setStatus('expired');
              return;
            }
          }

          if (res.ok) {
            const data = (await res.json()) as { userId: string };
            markAuthed(data.userId);
          }
        } catch (err) {
          console.error('[AuthBootstrap] /api/auth/tma failed:', err);
        }
      }

      const profileData = await apiClient.api.userControllerGetProfile();
      if (!isFresh()) return;
      setProfile(profileData);

      if (profileData.languageCode) {
        setLanguage(profileData.languageCode as Language);
      }

      qurvo.identify(profileData.id, {
        userId: profileData.id,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
      });

      // Тот же UUID, что в логах бэкенда и в payload команд, — по нему сессия
      // игрока в RUM сходится с его же запросами и трассировкой.
      datadogUser.set(profileData.id);

      if (!deepLinkAppliedRef.current) {
        deepLinkAppliedRef.current = true;
        const target = resolveDeepLinkTarget(
          startParam,
          !!profileData.hasOpenedCases,
          runtime,
        );
        if (isFresh()) {
          if (target) {
            router.replace(target);
          } else if (
            typeof window !== 'undefined' &&
            window.location.pathname === '/'
          ) {
            // TMA→web handoff: visitor arrived at the public marketing
            // root with `tgWebAppData=...` in the hash, AuthBootstrap
            // exchanged it for a cookie above. Move them to /home so
            // the authed shell renders instead of the anonymous
            // landing.
            router.replace('/home');
          }
        }
      }

      if (isFresh()) setStatus('ready');
    } catch (err: unknown) {
      if (!isFresh()) return;
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(t('errorProfileLoad', { error: message }));
      setStatus('error');
    }
  };

  useEffect(() => {
    // Runs on mount — boot no longer waits on Yandex Metrika config.
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, rawInitData]);

  let body: ReactNode;
  if (status === 'blocked') {
    body = <BlockedScreen />;
  } else if (status === 'expired') {
    body = <SessionExpiredScreen />;
  } else if (status === 'error') {
    body = (
      <div className="relative w-full h-full flex items-center justify-center bg-black">
        <ErrorMessage error={error} onRetry={() => void run()} />
      </div>
    );
  } else {
    body = children;
  }

  return (
    <AuthBootstrapContext.Provider value={{ status, error, retry: () => void run() }}>
      {body}
    </AuthBootstrapContext.Provider>
  );
}
